import { css } from '@emotion/react'
import { NextPage, NextPageContext } from 'next'
import { useState } from 'react'
import { Card, colors } from 'plume-ui'
import { CardInnerTitle } from '@/components/atoms/CardInnerTitle'
import { EmptyCard } from '@/components/common/EmptyCard'
import { SearchHead } from '@/components/common/Head'
import {
  ActiveTabMap,
  InViewForActiveTab,
  createInViewEventQueue,
  setupActiveTabs,
} from '@/components/common/InviewForActiveTab'
import { Template } from '@/components/common/Template'
import { FundsTable } from '@/components/common/FundsTable'
import { ViewMoreLink } from '@/components/common/ViewMoreLink'
import { InvestorsTable } from '@/components/pages/search/InvestorsTable'
import { SearchPeopleTable } from '@/components/pages/search/SearchPeopleTable'
import {
  SearchTabMenu,
  searchTabs,
} from '@/components/pages/search/SearchTabMenu'
import { VentureSupportTable } from '@/components/pages/search/VentureSupportTable'
import { getGlobalSearchResult, getSearchResult } from '@/lib/api/search'
import { fetchArticles, fetchArticlesByKeyword } from '@/lib/article'
import {
  Article,
  GlobalSearchResultData,
  OverseasCompaniesSearchResult,
  PeopleGlobalSearchResult,
  SearchResult,
  SearchTabType,
} from '../types'
import { useUser } from '../contexts/AuthContext'
import {
  isEnterpriseWithTrial,
  isTrial,
  isEnterprise,
  isLatestPlan,
} from '@/lib/user'
import { SearchCompanyCard } from '@/components/pages/search/SearchCompanyCard'
import { FullwidthArticleCard } from '@/components/common/FullwidthArticleCard'
import { A } from '@/components/atoms/A'
import { Table, TableCell } from '@/components/atoms/Table'
import { printError } from '@/lib/log'
import { useModal } from '../hooks/modal'
import { TrialModal } from '@/components/pages/company/molecules/TrialModal'

const NUM_OF_OVERSEAS_COMPANIES = 10

const [push, apply] = createInViewEventQueue()

type Props = {
  keyword?: string
  articles: Article[]
  overseasCompanies: OverseasCompaniesSearchResult
  people: PeopleGlobalSearchResult
} & SearchResult

const SearchPage: NextPage<Props> = (props) => {
  const user = useUser()

  const [init, first] = setupActiveTabs(searchTabs)
  const [activeTabs, setActiveTabs] =
    useState<ActiveTabMap<SearchTabType>>(init())

  const handleChangeActive = (tab: SearchTabType, inView: boolean) => {
    push(tab, inView, onTimeout)
  }

  const onTimeout = () => {
    setActiveTabs(apply(activeTabs))
  }

  const {
    isOpen: isTrialModalOpen,
    show: showTrialModal,
    hide: hideTrialModal,
  } = useModal({ open: false, key: 'trial' })
  const showModalForGuest: React.MouseEventHandler<HTMLAnchorElement> = (e) => {
    if (!isEnterpriseWithTrial(user)) {
      e.preventDefault()
      showTrialModal()
    }
  }

  return (
    <>
      <SearchHead />
      <Template>
        <div
          css={css`
            background-color: ${colors.gray40};
          `}
          className="search-result"
        >
          <SearchTabMenu activeTab={first(activeTabs)} {...props} />
          <div
            css={css`
              max-width: 1150px;
              margin: 0 auto;
              padding: 25px 0 50px;
            `}
          >
            <TrialModal isOpen={isTrialModalOpen} hideModal={hideTrialModal} />
            <SearchInviewForActiveTab
              tabName="スタートアップ"
              onChangeActive={handleChangeActive}
            >
              <Card className="domestic-companies">
                <CardInnerTitle title="国内スタートアップ" />
                {props.companies.count > 0 ? (
                  props.companies.data.map((company, i) => (
                    <SearchCompanyCard company={company} key={i} />
                  ))
                ) : (
                  <EmptyCard />
                )}
                {props.companies.count > 0 && (
                  <ViewMoreLink
                    isHidden={false}
                    count={props.companies.count}
                    href={
                      props.keyword
                        ? `/companies?keywords=${props.keyword}&country=JPN&sort=updated&order=desc`
                        : '/companies?country=JPN&sort=updated&order=desc'
                    }
                    prefetch={false}
                    onClick={showModalForGuest}
                  />
                )}
              </Card>
            </SearchInviewForActiveTab>
            {isTrial(user) || (isEnterprise(user) && isLatestPlan(user)) ? (
              <SearchInviewForActiveTab
                tabName="海外スタートアップ"
                onChangeActive={() => {}}
              >
                <OverseasCompaniesTabContent result={props.overseasCompanies} />
              </SearchInviewForActiveTab>
            ) : null}
            <SearchInviewForActiveTab
              tabName="投資家"
              onChangeActive={handleChangeActive}
            >
              <Card>
                <CardInnerTitle title="投資家" />
                <InvestorsTable investors={props.investors.data} />
                {props.investors.count > 0 && (
                  <ViewMoreLink
                    isHidden={false}
                    count={props.investors.count}
                    href={
                      props.keyword
                        ? `/investors?q=${props.keyword}`
                        : '/investors'
                    }
                    onClick={showModalForGuest}
                  />
                )}
              </Card>
            </SearchInviewForActiveTab>
            <SearchInviewForActiveTab
              tabName="オリジナル記事"
              onChangeActive={handleChangeActive}
            >
              <Card>
                <CardInnerTitle title="オリジナル記事" />
                {props.articles.length > 0 ? (
                  <>
                    {props.articles.map((article) => (
                      <FullwidthArticleCard
                        key={article.id}
                        article={article}
                      />
                    ))}
                  </>
                ) : (
                  <EmptyCard />
                )}
              </Card>
            </SearchInviewForActiveTab>
            <SearchInviewForActiveTab
              tabName="ファンド"
              onChangeActive={handleChangeActive}
            >
              <Card>
                <CardInnerTitle title="ファンド" />
                {isEnterpriseWithTrial(user) ? (
                  <>
                    <FundsTable list={props.funds.data} />
                    <ViewMoreLink
                      isHidden={props.funds.count === 0}
                      href={
                        props.keyword
                          ? `/funds?fund_search&keywords=${props.keyword}`
                          : '/funds'
                      }
                      count={props.funds.count}
                    />
                  </>
                ) : (
                  <Card>
                    <Card.Content>
                      <div
                        css={css`
                          display: flex;
                          align-items: center;
                          justify-content: center;
                          color: #999;
                          font-size: 14px;
                          padding: 5px;
                        `}
                      >
                        有料コンテンツ
                      </div>
                    </Card.Content>
                  </Card>
                )}
              </Card>
            </SearchInviewForActiveTab>
            <SearchInviewForActiveTab
              tabName="人物"
              onChangeActive={handleChangeActive}
            >
              <Card>
                <CardInnerTitle title="人物" />
                <SearchPeopleTable list={props.people.data} />
                <ViewMoreLink
                  isHidden={props.people.count === 0}
                  href={
                    props.keyword ? `/people?q=${props.keyword}` : '/people'
                  }
                  count={props.people.count}
                  onClick={showModalForGuest}
                  dataTestId="people-view-more"
                />
              </Card>
            </SearchInviewForActiveTab>
            <SearchInviewForActiveTab
              tabName="ベンチャー支援"
              onChangeActive={handleChangeActive}
            >
              <Card>
                <CardInnerTitle title="ベンチャー支援" />
                <VentureSupportTable list={props.ventureSupports.data} />
                <ViewMoreLink
                  isHidden={props.ventureSupports.count === 0}
                  href={
                    props.keyword
                      ? `/venture_supports?q=${props.keyword}`
                      : '/venture_supports'
                  }
                  count={props.ventureSupports.count}
                  onClick={showModalForGuest}
                />
              </Card>
            </SearchInviewForActiveTab>
          </div>
        </div>
      </Template>
    </>
  )
}

type SearchInviewForActiveTabProps = {
  tabName: SearchTabType
  children: React.ReactNode
  onChangeActive: (tab: SearchTabType, inView: boolean) => void
}

const SearchInviewForActiveTab: React.FC<SearchInviewForActiveTabProps> = (
  props
) => {
  return (
    <InViewForActiveTab
      {...props}
      offset="-10rem"
      css={css`
        margin-bottom: 70px;
      `}
    />
  )
}

const OverseasCompaniesTabContent: React.VFC<{
  result: OverseasCompaniesSearchResult
}> = (props) => {
  const items = props.result.data.slice(0, NUM_OF_OVERSEAS_COMPANIES)
  return (
    <Card className="overseas-companies">
      <CardInnerTitle title="海外スタートアップ" />
      {items.length > 0 ? (
        <Table>
          <tbody>
            {items.map((company, i) => (
              <tr className="company" key={i}>
                <TableCell>
                  <A
                    className="name"
                    decoration="none"
                    key={i}
                    href={`/companies/${company.globalId}`}
                  >
                    {company.name}
                  </A>
                </TableCell>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <EmptyCard />
      )}
    </Card>
  )
}

SearchPage.getInitialProps = async (ctx: NextPageContext) => {
  redirectLegacyParams(ctx)
  const keyword = ctx.query.q?.toString()
  const searchResult = await getGlobalSearch(ctx, keyword)

  const articles = keyword
    ? await fetchArticlesByKeyword(keyword)
    : await fetchArticles(0, 5)

  return {
    ...searchResult,
    keyword,
    articles,
  }
}

// ep時代のクエリパラメータでページにランディングした場合リダイレクトする
const redirectLegacyParams = (ctx: NextPageContext) => {
  const queryValue = ctx.query['search[keywords]']
  if (queryValue) {
    const v = encodeURIComponent(queryValue.toString())
    ctx.res!.writeHead(301, {
      Location: `/search?q=${v}`,
    })
    ctx.res!.end()
  }
}

const emptySearchResult = {
  companies: { count: 0, data: [] },
  investors: { count: 0, data: [] },
  funds: { count: 0, data: [] },
  people: { count: 0, data: [] },
  ventureSupports: { count: 0, data: [] },
}

const emptyGlobalSearchResult: GlobalSearchResultData = {
  overseasCompanies: { count: 0, data: [] },
  people: { count: 0, data: [] },
}

const getGlobalSearch = async (ctx: NextPageContext, keyword?: string) => {
  const [result, rawGlobalSearchResult] = await Promise.all([
    getSearchResult(keyword).catch((e) => printError(e)),
    getGlobalSearchResult(ctx, keyword).catch((e) => printError(e)),
  ])
  const searchResult = result ? result : emptySearchResult
  const globalSearchResult: GlobalSearchResultData = rawGlobalSearchResult
    ? {
        overseasCompanies: rawGlobalSearchResult.data.overseasCompanies,
        people: rawGlobalSearchResult.data.people,
      }
    : emptyGlobalSearchResult

  return {
    ...searchResult,
    ...globalSearchResult,
  }
}

export default SearchPage
